import * as React from 'react';
import * as Router from 'react-router-dom';

import './ConnectFolder.css';
import { dedent, sluggify } from 'frontend/utils';
import { $, $$, A, T, Hooks } from 'frontend';
import { InfoLink } from './InfoLink';

export function ConnectFolder() {
  const experimentalFeatures = $$.getExperimentalFeatures();
  const { dispatch, getState } = Hooks.useStore();
  const navigate = Router.useNavigate();
  const nameRef = React.useRef<null | HTMLInputElement>(null);
  const urlRef = React.useRef<null | HTMLInputElement>(null);
  const { error, setError } = Hooks.useError();
  const [storeType, setStoreType] =
    React.useState<T.FileStoreServer['storeType']>('files');

  function addFileServer(event: Event) {
    event.preventDefault();
    setError(null);
    const servers = $.getServers(getState());
    const name = nameRef.current?.value;
    const url = urlRef.current?.value;
    const server = validateFileStoreServer(
      name,
      url,
      servers,
      setError,
      storeType,
    );
    if (server) {
      dispatch(A.setHasOnboarded(true));
      dispatch(A.addFileStoreServer(server));
      dispatch(A.changeFileStore('server', server));
      navigate('/');
    }
  }

  return (
    <div className="page">
      <div className="pageInner">
        <h1>
          Connect a Folder
          <span className="pageBeta">Beta</span>
        </h1>
        <h2>
          Local &amp; Network Storage{' '}
          <InfoLink
            href="/docs/file-store-server.html"
            title="Access folders from your computer, home server, or NAS."
          />
        </h2>
        <p>
          Whether you are accessing files stored locally on your machine, or you
          want to access files on your NAS (Network Attached Storage), you can
          run the storage server and connect a folder on a machine you have
          access to.
        </p>

        <h3>1: Start the server</h3>
        <pre className="connectFolderPre">
          {dedent`
              git clone git@github.com:gregtatum/indie-web.git
              cd indie-web/src/server
              npm install
              npm start
          `}
        </pre>
        <p>Follow the installation instructions.</p>

        <h3>2: Connect the Folder</h3>
        <form onSubmit={(event) => addFileServer(event.nativeEvent)}>
          <div className="connectFolderInput">
            <label htmlFor="storage-provider-name">Name</label>
            <input
              type="text"
              id="storage-provider-name"
              placeholder="NAS Storage"
              ref={nameRef}
              maxLength={200}
            ></input>
          </div>
          <div className="connectFolderInput">
            <label htmlFor="storage-provider-address">Server Address</label>
            <input
              type="text"
              id="storage-provider-address"
              ref={urlRef}
              defaultValue="http://localhost:6543"
            ></input>
          </div>
          {experimentalFeatures ? (
            <div className="connectFolderInput">
              <label>Storage Type</label>
              <div className="connectFolderRadioGroup">
                <label>
                  <input
                    type="radio"
                    name="storeType"
                    value="files"
                    checked={storeType === 'files'}
                    onChange={() => setStoreType('files')}
                  />
                  {' File Storage'}
                </label>
                <label>
                  <input
                    type="radio"
                    name="storeType"
                    value="music"
                    checked={storeType === 'music'}
                    onChange={() => setStoreType('music')}
                  />
                  {' Music Storage '}
                  <span className="pageBeta pageExperimental">
                    Experimental
                  </span>
                </label>
              </div>
              {storeType === 'music' ? (
                <p className="connectFolderNote">
                  Point the server&apos;s mount at your music folder. To use
                  multiple music folders, run a separate server instance for
                  each one.
                </p>
              ) : null}
            </div>
          ) : null}
          {error}
          <button type="submit">Connect Folder</button>
        </form>
      </div>
    </div>
  );
}

interface ConnectedFoldersSettingsProps {
  fileStoreServers: T.FileStoreServer[];
  nonServerFallbackStorage?: T.FileStoreName | null;
}

export function ConnectedFoldersSettings({
  fileStoreServers,
  nonServerFallbackStorage = null,
}: ConnectedFoldersSettingsProps) {
  const { dispatch, getState } = Hooks.useStore();
  const navigate = Router.useNavigate();
  const { error, setError } = Hooks.useError();

  if (fileStoreServers.length === 0) {
    return null;
  }
  function update(event: Event, oldServer: T.FileStoreServer) {
    event.preventDefault();
    const { target } = event;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const form = target.closest('form');
    if (!form) {
      return;
    }
    const [nameInput, urlInput] = form.querySelectorAll('input');
    const otherServers = $.getServers(getState()).filter(
      (server) =>
        server.name !== oldServer.name && server.url !== oldServer.url,
    );
    if (
      oldServer.name === nameInput.value &&
      oldServer.url === urlInput.value
    ) {
      // Nothing changed.
      return;
    }
    const server = validateFileStoreServer(
      nameInput.value,
      urlInput.value,
      otherServers,
      setError,
      oldServer.storeType,
    );
    if (server) {
      dispatch(A.updateFileStoreServer(oldServer, server));
      dispatch(
        A.addMessage({
          message: <>Folder updated</>,
          timeout: true,
        }),
      );
    }
  }

  return (
    <>
      {error}
      <div className="connectedFolderList">
        {fileStoreServers.map((server) => (
          <form
            key={server.id}
            onSubmit={(event) => update(event.nativeEvent, server)}
          >
            <input
              type="text"
              defaultValue={server.name}
              onBlur={(event) => update(event.nativeEvent, server)}
            ></input>
            <input
              type="text"
              defaultValue={server.url}
              onBlur={(event) => update(event.nativeEvent, server)}
            ></input>
            <input type="submit" style={{ display: 'none' }}></input>
            <button
              type="button"
              onClick={() => {
                if (confirm('Are you sure you want to remove this folder?')) {
                  const nextServer =
                    fileStoreServers.find(
                      (candidate) => candidate.id !== server.id,
                    ) ?? null;
                  const isRemovingCurrentServer =
                    $.getServerId(getState()) === server.id;

                  if (!nonServerFallbackStorage && !nextServer) {
                    dispatch(A.removeAllStorage());
                    navigate('/');
                    return;
                  }

                  dispatch(A.removeFileStoreServer(server));
                  if (isRemovingCurrentServer) {
                    if (nonServerFallbackStorage) {
                      dispatch(A.changeFileStore(nonServerFallbackStorage));
                    } else if (nextServer) {
                      dispatch(A.changeFileStore('server', nextServer));
                    }
                  }
                }
              }}
            >
              Remove
            </button>
          </form>
        ))}
      </div>
    </>
  );
}

function validateFileStoreServer(
  name: string | undefined,
  url: string | undefined,
  servers: T.FileStoreServer[],
  setError: (error: string) => void,
  storeType: T.FileStoreServer['storeType'] = 'files',
): null | T.FileStoreServer {
  // Make sure all the values are set.
  if (!name) {
    setError('The name of the server must be set');
    return null;
  }
  if (!url) {
    setError('The address of the server must be set');
    return null;
  }

  const id = sluggify(name);

  // Validate the URL.
  try {
    new URL(url);
  } catch {
    url = 'http://' + url;
    try {
      new URL(url);
    } catch {
      setError('The address of the server was not a valid URL.');
      return null;
    }
  }

  // Strip trailing slashes so that URLs built by concatenation (e.g.
  // `${server.url}/music/music-index`) don't end up with a double slash.
  url = url.replace(/\/+$/, '');

  if (name.length > 200) {
    setError('The server name should be less than 200 characters.');
    return null;
  }

  if (servers.some((server) => server.id === id)) {
    setError(`Another server already has that name "${name}" or slug "${id}"`);
    return null;
  }
  const existingURL = servers.find((server) => server.url === url);
  if (existingURL) {
    setError(`The server "${existingURL.name}" already uses that URL.`);
    return null;
  }

  return { name, url, id, storeType };
}
